import useBeneficiaryAccess from '@UI/Hooks/useBeneficiaryAccess';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Message, Spinner } from '@verclam/ui-kit';

const spinnerContainerStyle = css`
	display: flex;
	justify-content: center;
	padding: ${theme.layout.gutter};
`;

const messageContainerStyle = css`
	padding: ${theme.layout.gutter};
`;

type Props = {
	/** French description of the action being attempted, shown in the refusal message. */
	readonly action: string;
	readonly children: React.ReactNode;
};

/**
 * Route guard for reading beneficiary dossiers: waits for the caller's level on the beneficiary
 * type before deciding, since an absent level is indistinguishable from a refusal while it loads.
 */
const RequireBeneficiaryAccess: React.FC<Props> = ({ action, children }) => {
	const { access, isLoading, error } = useBeneficiaryAccess();

	if (error !== null) {
		return (
			<div css={messageContainerStyle}>
				<Message
					severity="error"
					text="Impossible de vérifier vos droits d'accès aux dossiers. Rechargez la page."
				/>
			</div>
		);
	}

	if (isLoading || access === null) {
		return (
			<div css={spinnerContainerStyle}>
				<Spinner aria-label="Vérification des droits d'accès" />
			</div>
		);
	}

	if (!access.canRead) {
		return (
			<div css={messageContainerStyle}>
				<Message
					severity="error"
					text={`Vous n'avez pas le droit nécessaire pour ${action} (accès au type de dossier requis).`}
				/>
			</div>
		);
	}

	return children;
};

export default RequireBeneficiaryAccess;
